import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Check,
  ChevronsUpDown,
  CircleHelp,
  Eye,
  Image as ImageIcon,
  Type,
  X,
} from "lucide-react";
import { PromptModelSelect } from "@/features/prompts/components/PromptModelSelect";
import { PromptRowActions } from "@/features/prompts/components/PromptRowActions";
import {
  GLOBAL_TENANT_OPTION_ID,
  usePromptTenantOptions,
} from "@/features/prompts/hooks/usePromptTenantOptions";
import { shouldKeepPromptEditorOpen } from "@/features/prompts/lib/prompt-editor-overlay";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Button,
  Input,
  Label,
  Switch,
  SearchableCombobox,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  PromptEditor,
  VersionTimeline,
  MultiTagInput,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  cn,
} from "@nexus/ui";
import type { VersionEntry } from "@nexus/ui";
import {
  PROMPT_USED_IN_SUGGESTIONS,
  fromPromptTaskTypeDb,
  getPromptModelSuggestion,
  getPromptTaskTypeForUsedIn,
  isPromptModelSupported,
  promptModelIsHonoured,
  resolvePromptModelSuggestion,
  toPromptTaskTypeDb,
  type PromptTaskType,
} from "@nexus/contracts";
import type { PromptTaskTypeDto } from "@/features/prompts/api/prompts.api";
import {
  promptDetailQueryOptions,
  promptLabelsQueryOptions,
  promptVersionsQueryOptions,
  promptUsedInValuesQueryOptions,
  promptVariablesQueryOptions,
  promptsLlmModelsQueryOptions,
  promptsKeys,
} from "@/lib/queries";
import type { PromptDto } from "@/lib/queries";
import {
  createPrompt,
  updatePrompt,
  createPromptVersion,
  revertPromptVersion,
  refreshPromptLlmModels,
} from "@/features/prompts/api/prompts.api";
import { getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import { notifyPromptVersionError } from "@/features/prompts/lib/prompt-version-error";
import { getPromptModelTierWarning } from "@/features/prompts/lib/prompt-model-ui";
import { isSameVersionContent } from "@/features/prompts/lib/version-identity";
import { VariableUsageButton } from "@/features/prompts/components/VariableUsageButton";

// ─── Types ───────────────────────────────────────────────────────────

const VARIABLE_REF_REGEX = /\{\{(\w+)\}\}/g;

const TASK_TYPE_OPTIONS = [
  {
    value: "text",
    Icon: Type,
    labelKey: "prompts.editor.taskType.text",
    labelFallback: "Text",
    hintKey: "prompts.editor.taskType.hintText",
    hintFallback: "Text in → text out",
  },
  {
    value: "vision",
    Icon: Eye,
    labelKey: "prompts.editor.taskType.vision",
    labelFallback: "Vision",
    hintKey: "prompts.editor.taskType.hintVision",
    hintFallback: "Video / PDF / image in → text out",
  },
  {
    value: "image",
    Icon: ImageIcon,
    labelKey: "prompts.editor.taskType.image",
    labelFallback: "Image",
    hintKey: "prompts.editor.taskType.hintImage",
    hintFallback: "Text in → image out",
  },
] as const satisfies ReadonlyArray<{
  value: PromptTaskType;
  Icon: typeof Type;
  labelKey: string;
  labelFallback: string;
  hintKey: string;
  hintFallback: string;
}>;

/** Indexed by the active task so the segmented-control thumb slides on transform alone. */
const TASK_THUMB_OFFSET = [
  "translate-x-0",
  "translate-x-full",
  "translate-x-[200%]",
] as const;

const EASE_OUT_QUINT = "ease-[cubic-bezier(0.22,1,0.36,1)]";

interface PromptEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptId: string | null;
  prompt: PromptDto | null;
  mode: "create" | "edit";
  onSaved: () => void;
  /** Row dialogs live on PromptsPage so they outlive this sheet. */
  onDuplicate?: (prompt: PromptDto) => void;
  onDelete?: (prompt: PromptDto) => void;
  /** 1-based line the Monaco editor should reveal. */
  revealLine?: number | null;
  onOpenPrompt?: (promptId: string) => void;
  onOpenReplace?: (find: string) => void;
}

function SpecLabel({
  htmlFor,
  children,
  trailing,
}: {
  htmlFor?: string;
  children: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex h-5 items-center justify-between gap-2">
      <Label
        htmlFor={htmlFor}
        className="text-[12px] font-medium tracking-[-0.01em] text-muted-foreground"
      >
        {children}
      </Label>
      {trailing}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────

export function PromptEditorSheet({
  open,
  onOpenChange,
  promptId,
  prompt,
  mode,
  onSaved,
  onDuplicate,
  onDelete,
  revealLine,
  onOpenPrompt,
  onOpenReplace,
}: PromptEditorSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);
  const [usedInOpen, setUsedInOpen] = useState(false);
  const [usedInSearch, setUsedInSearch] = useState("");

  const tenantOptions = usePromptTenantOptions();

  // ── Form state ─────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [usedIn, setUsedIn] = useState<string>("");
  const [taskType, setTaskType] = useState<PromptTaskType>("text");
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [model, setModel] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [variables, setVariables] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [taskTypeClearedModel, setTaskTypeClearedModel] = useState(false);

  // ── Queries ────────────────────────────────────────────────────────
  const { data: detail } = useQuery({
    ...promptDetailQueryOptions(promptId ?? ""),
    enabled: !!promptId && mode === "edit",
  });
  const rowPrompt = prompt ?? detail ?? null;

  const { data: versions = [] } = useQuery({
    ...promptVersionsQueryOptions(promptId ?? ""),
    enabled: !!promptId && mode === "edit",
  });

  const { data: existingUsedInValues = [] } = useQuery(
    promptUsedInValuesQueryOptions(),
  );

  const { data: knownLabels = [] } = useQuery({
    ...promptLabelsQueryOptions(),
    enabled: open,
  });

  const { data: llmModelsResponse, isLoading: isLoadingModels } = useQuery({
    ...promptsLlmModelsQueryOptions(),
    enabled: open,
  });
  const llmModels = useMemo(
    () => llmModelsResponse?.models ?? [],
    [llmModelsResponse],
  );

  // Variables registry for the current usedIn context (for pill suggestions)
  const effectiveUsedIn = mode === "edit" ? detail?.usedIn : usedIn;
  const { data: registryVariables = [] } = useQuery(
    promptVariablesQueryOptions(effectiveUsedIn ?? ""),
  );

  // Merge DB values + suggestions, deduplicated
  const usedInOptions = useMemo(() => {
    const set = new Set([...existingUsedInValues, ...PROMPT_USED_IN_SUGGESTIONS]);
    return Array.from(set).sort();
  }, [existingUsedInValues]);

  const suggestedModel = effectiveUsedIn
    ? resolvePromptModelSuggestion(effectiveUsedIn, llmModels, taskType)
    : null;
  const recommendation = effectiveUsedIn
    ? getPromptModelSuggestion(effectiveUsedIn)
    : null;
  const selectedCatalogModel = model
    ? (llmModels.find((m) => m.id === model) ??
      (suggestedModel?.id === model ? suggestedModel : null))
    : null;
  const tierWarning = getPromptModelTierWarning(selectedCatalogModel?.tier, effectiveUsedIn ?? "");
  const modelMissingFromCatalog = Boolean(
    model && !llmModels.some((m) => m.id === model) && suggestedModel?.id !== model,
  );
  const modelIncapableForTask = Boolean(
    selectedCatalogModel && !selectedCatalogModel.capabilities.includes(taskType),
  );
  const modelUnavailable = Boolean(selectedCatalogModel?.unavailable);
  // A model the call site's worker cannot reach would be dropped at run time in
  // favour of the service default, so say so here rather than at 3am in a log.
  const modelWrongProvider = Boolean(
    model && effectiveUsedIn && !isPromptModelSupported(effectiveUsedIn, model),
  );
  const modelIsIgnoredHere = Boolean(
    effectiveUsedIn && !promptModelIsHonoured(effectiveUsedIn),
  );

  // ── Sync form from loaded data ─────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && detail) {
      setName(detail.name);
      setUsedIn(detail.usedIn);
      setTaskType(fromPromptTaskTypeDb(detail.taskType));
      setContent(detail.latestVersion?.content ?? "");
      setModel(detail.latestVersion?.model ?? null);
      setVariables(detail.latestVersion?.variables ?? []);
      setLabels(detail.labels ?? []);
      setIsActive(detail.status === "ACTIVE");
      setViewingVersionId(null);
      setTaskTypeClearedModel(false);
    } else if (mode === "create") {
      setName("");
      setUsedIn("");
      setTaskType("text");
      setTenantId(null);
      setContent("");
      setModel(null);
      setVariables([]);
      setLabels([]);
      setIsActive(true);
      setViewingVersionId(null);
      setTaskTypeClearedModel(false);
    }
  }, [mode, detail]);

  const applyUsedInSelection = (nextUsedIn: string) => {
    setUsedIn(nextUsedIn);
    const nextTask = getPromptTaskTypeForUsedIn(nextUsedIn);
    setTaskType(nextTask);
    setTaskTypeClearedModel(false);
    if (mode !== "create") return;
    const nextSuggested = resolvePromptModelSuggestion(nextUsedIn, llmModels, nextTask);
    setModel(nextSuggested?.id ?? null);
  };

  const applyTaskTypeSelection = (nextTask: PromptTaskType) => {
    setTaskType(nextTask);
    if (model) {
      const current = llmModels.find((m) => m.id === model);
      if (current && !current.capabilities.includes(nextTask)) {
        setModel(null);
        setTaskTypeClearedModel(true);
        return;
      }
    }
    setTaskTypeClearedModel(false);
    if (mode === "create" && usedIn) {
      const nextSuggested = resolvePromptModelSuggestion(usedIn, llmModels, nextTask);
      if (nextSuggested) setModel(nextSuggested.id);
    }
  };

  // Create mode: when catalog arrives after usedIn was chosen, apply suggestion once.
  useEffect(() => {
    if (mode !== "create" || !usedIn || model != null || llmModels.length === 0) return;
    const nextSuggested = resolvePromptModelSuggestion(usedIn, llmModels, taskType);
    if (nextSuggested) setModel(nextSuggested.id);
  }, [mode, usedIn, model, llmModels, taskType]);

  const isDirty = useMemo(() => {
    if (mode === "create") {
      return Boolean(
        name.trim() || usedIn.trim() || content.trim() || model || variables.length > 0 || labels.length > 0,
      );
    }
    if (!detail) return false;
    const metaChanged =
      name !== detail.name ||
      isActive !== (detail.status === "ACTIVE") ||
      taskType !== fromPromptTaskTypeDb(detail.taskType);
    const contentChanged = content !== (detail.latestVersion?.content ?? "");
    const modelChanged = model !== (detail.latestVersion?.model ?? null);
    const variablesChanged =
      JSON.stringify([...variables].sort()) !==
      JSON.stringify([...(detail.latestVersion?.variables ?? [])].sort());
    const labelsChanged = JSON.stringify(labels) !== JSON.stringify(detail.labels ?? []);
    return metaChanged || contentChanged || modelChanged || variablesChanged || labelsChanged;
  }, [mode, name, usedIn, content, model, variables, labels, isActive, taskType, detail]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      const discard = window.confirm(
        t(
          "prompts.editor.dirtyCloseConfirm",
          "You have unsaved changes. Discard them and close?",
        ),
      );
      if (!discard) return;
    }
    onOpenChange(false);
  }, [isDirty, onOpenChange, t]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        requestClose();
        return;
      }
      onOpenChange(true);
    },
    [onOpenChange, requestClose],
  );

  // ── Auto-sync: parse {{var}} from content → add to variables (additive only) ──
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const matches = new Set<string>();
      let m: RegExpExecArray | null;
      const re = new RegExp(VARIABLE_REF_REGEX.source, "g");
      while ((m = re.exec(content)) !== null) matches.add(m[1]);
      if (matches.size === 0) return;

      setVariables((prev) => {
        const existing = new Set(prev);
        const merged = [...prev];
        for (const name of matches) {
          if (!existing.has(name)) merged.push(name);
        }
        return merged.length === prev.length ? prev : merged;
      });
    }, 300);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [content]);

  // ── Mutations ──────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: () =>
      createPrompt({
        name,
        usedIn,
        tenantId,
        content,
        model,
        taskType: toPromptTaskTypeDb(taskType) as PromptTaskTypeDto,
        variables,
        labels,
      }),
    onSuccess: (result) => {
      toast.success(t("prompts.editor.toast.created", "Prompt created"));
      if (result.globalFallbackCreated) {
        toast.info(
          t(
            "prompts.editor.toast.globalFallbackCreated",
            'A global fallback prompt was also created for "{{usedIn}}"',
            { usedIn },
          ),
        );
      }
      void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
      onSaved();
      onOpenChange(false);
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err, passThroughMessage);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });

  const updateMetaMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      status?: string;
      taskType?: PromptTaskTypeDto;
      labels?: string[];
    }) => updatePrompt(promptId!, data),
    onSuccess: () => {
      toast.success(t("prompts.editor.toast.updated", "Prompt updated"));
      void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
      onSaved();
    },
    onError: () =>
      toast.error(t("prompts.editor.toast.updateFailed", "Failed to update prompt")),
  });

  const createVersionMutation = useMutation({
    mutationFn: (payload: {
      content: string;
      model: string | null;
      taskType?: PromptTaskTypeDto;
    }) =>
      createPromptVersion(promptId!, {
        content: payload.content,
        model: payload.model,
        taskType: payload.taskType,
        variables,
        ...(detail?.latestVersion?.id
          ? { expectedVersionId: detail.latestVersion.id }
          : {}),
      }),
    onSuccess: () => {
      toast.success(t("prompts.editor.toast.versionSaved", "New version saved"));
      void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
      onSaved();
    },
    onError: (err) => notifyPromptVersionError(err, t, queryClient, "save"),
  });

  const revertMutation = useMutation({
    mutationFn: (versionId: string) =>
      revertPromptVersion(promptId!, versionId),
    onSuccess: (newVersion) => {
      toast.success(
        t("prompts.editor.toast.reverted", "Reverted — created v{{version}}", {
          version: newVersion.version,
        }),
      );
      setContent(newVersion.content);
      setModel(newVersion.model ?? null);
      setVariables(newVersion.variables ?? []);
      setViewingVersionId(null);
      void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
      onSaved();
    },
    onError: (err) => notifyPromptVersionError(err, t, queryClient, "revert"),
  });

  const refreshModelsMutation = useMutation({
    mutationFn: () => refreshPromptLlmModels(),
    onSuccess: (data) => {
      queryClient.setQueryData(promptsKeys.llmModels, data);
      toast.success(t("prompts.editor.toast.modelsRefreshed", "Model catalog refreshed"));
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err, passThroughMessage);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });

  // ── Handlers ───────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const modelChanged =
      mode === "create"
        ? model != null
        : model !== (detail?.latestVersion?.model ?? null);
    if ((modelMissingFromCatalog || modelIncapableForTask) && (mode === "create" || modelChanged)) {
      toast.error(
        t(
          modelIncapableForTask
            ? "prompts.editor.toast.modelIncapable"
            : "prompts.editor.toast.modelNotInCatalog",
          modelIncapableForTask
            ? "Selected model cannot perform this task. Pick another before saving."
            : "Selected model is not in the catalog. Pick another model before saving.",
        ),
      );
      return;
    }

    if (mode === "create") {
      createMutation.mutate();
      return;
    }

    // Edit mode: check what changed
    const taskTypeChanged = taskType !== fromPromptTaskTypeDb(detail!.taskType);
    const nameOrStatusChanged =
      name !== detail?.name || isActive !== (detail?.status === "ACTIVE");
    const contentChanged = content !== (detail?.latestVersion?.content ?? "");
    const variablesChanged =
      JSON.stringify([...variables].sort()) !==
      JSON.stringify([...(detail?.latestVersion?.variables ?? [])].sort());
    const labelsChanged = JSON.stringify(labels) !== JSON.stringify(detail?.labels ?? []);
    const versionFieldsChanged = contentChanged || variablesChanged || modelChanged;
    const labelPatch = labelsChanged ? { labels } : {};

    // taskType + model must be one logical update — the version route accepts
    // taskType and validates against the final pair in a single transaction.
    if (taskTypeChanged && versionFieldsChanged) {
      createVersionMutation.mutate({
        content,
        model,
        taskType: toPromptTaskTypeDb(taskType) as PromptTaskTypeDto,
      });
      if (nameOrStatusChanged || labelsChanged) {
        updateMetaMutation.mutate({
          ...(name !== detail?.name ? { name } : {}),
          ...(isActive !== (detail?.status === "ACTIVE")
            ? { status: isActive ? "ACTIVE" : "INACTIVE" }
            : {}),
          ...labelPatch,
        });
      }
    } else {
      if (nameOrStatusChanged || taskTypeChanged || labelsChanged) {
        updateMetaMutation.mutate({
          ...(name !== detail?.name ? { name } : {}),
          ...(isActive !== (detail?.status === "ACTIVE")
            ? { status: isActive ? "ACTIVE" : "INACTIVE" }
            : {}),
          ...(taskTypeChanged
            ? { taskType: toPromptTaskTypeDb(taskType) as PromptTaskTypeDto }
            : {}),
          ...labelPatch,
        });
      }

      if (versionFieldsChanged) {
        createVersionMutation.mutate({ content, model });
      }
    }

    if (
      !nameOrStatusChanged &&
      !taskTypeChanged &&
      !contentChanged &&
      !variablesChanged &&
      !modelChanged &&
      !labelsChanged
    ) {
      toast.info(t("prompts.editor.toast.noChanges", "No changes to save"));
    }
  }, [
    mode,
    name,
    content,
    model,
    variables,
    labels,
    isActive,
    taskType,
    detail,
    modelMissingFromCatalog,
    modelIncapableForTask,
    createMutation,
    updateMetaMutation,
    createVersionMutation,
    t,
  ]);

  const handleVersionSelect = useCallback(
    (v: VersionEntry) => {
      const version = versions.find((ver) => ver.id === v.id);
      if (version) {
        setViewingVersionId(version.id);
        setContent(version.content);
        setModel(version.model ?? null);
        setVariables(version.variables ?? []);
      }
    },
    [versions],
  );

  const handleRevert = useCallback(
    (v: VersionEntry) => {
      revertMutation.mutate(v.id);
    },
    [revertMutation],
  );

  const isSaving =
    createMutation.isPending ||
    updateMetaMutation.isPending ||
    createVersionMutation.isPending;

  const activeTaskIndex = Math.max(
    0,
    TASK_TYPE_OPTIONS.findIndex((option) => option.value === taskType),
  );

  const serviceDefaultLabel = t(
    "prompts.editor.model.serviceDefault",
    "Service default",
  );

  const versionEntries: VersionEntry[] = useMemo(() => {
    const labelById = new Map(llmModels.map((m) => [m.id, m.label]));
    const latest = versions[0];
    return versions.map((v) => ({
      id: v.id,
      version: v.version,
      createdAt: v.createdAt,
      createdBy: v.createdBy,
      meta: v.model ? (labelById.get(v.model) ?? v.model) : serviceDefaultLabel,
      isCurrentState: isSameVersionContent(v, latest),
    }));
  }, [versions, llmModels, serviceDefaultLabel]);

  const latestVersionId = versions[0]?.id;
  const viewedVersion = versions.find((v) => v.id === viewingVersionId);
  const isViewingHistory = Boolean(viewedVersion && viewedVersion.id !== latestVersionId);

  const handleBackToLatest = useCallback(() => {
    setViewingVersionId(null);
    setContent(detail?.latestVersion?.content ?? "");
    setModel(detail?.latestVersion?.model ?? null);
    setVariables(detail?.latestVersion?.variables ?? []);
  }, [detail]);

  const modelBlocksSave =
    (modelMissingFromCatalog || modelIncapableForTask || modelUnavailable) &&
    (mode === "create" || model !== (detail?.latestVersion?.model ?? null));

  const saveBlockedReason = useMemo(() => {
    if (!name.trim()) {
      return t("prompts.editor.requiredName", "Name is required");
    }
    if (!content.trim()) {
      return t("prompts.editor.requiredContent", "Content is required");
    }
    if (modelBlocksSave) {
      if (modelUnavailable) {
        return t(
          "prompts.editor.model.warningUnavailable",
          "This project cannot call this model — ask an admin to enable it in Vertex, or pick another.",
        );
      }
      return modelIncapableForTask
        ? t(
            "prompts.editor.model.warningIncapable",
            "This model cannot perform the selected task.",
          )
        : t(
            "prompts.editor.model.warningRetired",
            "Not in catalog — pick another before saving.",
          );
    }
    return null;
  }, [name, content, modelBlocksSave, modelIncapableForTask, modelUnavailable, t]);

  const modelHelper = useMemo<{
    tone: "warning" | "muted";
    message: string;
  } | null>(() => {
    if (modelIsIgnoredHere) {
      return {
        tone: "muted",
        message: t(
          "prompts.editor.model.notUsedHere",
          "This call site takes its model from its flow settings — a model picked here is ignored.",
        ),
      };
    }
    if (modelUnavailable) {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningUnavailable",
          "This project cannot call this model — ask an admin to enable it in Vertex, or pick another.",
        ),
      };
    }
    if (modelWrongProvider) {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningWrongProvider",
          "This call site cannot run this model — it would fall back to the service default.",
        ),
      };
    }
    if (modelMissingFromCatalog) {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningRetired",
          "Not in catalog — pick another before saving.",
        ),
      };
    }
    if (modelIncapableForTask) {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningIncapable",
          "This model cannot perform the selected task.",
        ),
      };
    }
    if (taskTypeClearedModel) {
      return {
        tone: "muted",
        message: t(
          "prompts.editor.model.clearedOnTaskChange",
          "Previous model cleared — it could not perform this task.",
        ),
      };
    }
    if (tierWarning === "over") {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningOver",
          "Premium model — higher cost",
        ),
      };
    }
    if (tierWarning === "under") {
      return {
        tone: "warning",
        message: t(
          "prompts.editor.model.warningUnder",
          "May reduce quality for this workload",
        ),
      };
    }
    if (recommendation && suggestedModel && !model) {
      return {
        tone: "muted",
        message: t(
          "prompts.editor.model.recommendedHelper",
          "Suggested: {{label}} · {{costHint}}",
          { label: suggestedModel.label, costHint: suggestedModel.costHint },
        ),
      };
    }
    return null;
  }, [
    model,
    modelIsIgnoredHere,
    modelUnavailable,
    modelWrongProvider,
    modelMissingFromCatalog,
    modelIncapableForTask,
    recommendation,
    suggestedModel,
    t,
    tierWarning,
    taskTypeClearedModel,
  ]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        showClose={false}
        className="flex w-full max-w-[960px] flex-col gap-0 p-0 sm:max-w-[960px]"
        onPointerDownOutside={(event) => {
          if (shouldKeepPromptEditorOpen(event.target)) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (shouldKeepPromptEditorOpen(event.target)) {
            event.preventDefault();
          }
        }}
      >
        <SheetHeader className="shrink-0 gap-0 px-5 pb-2 pt-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-0.5">
              <SheetTitle className="truncate text-[15px] font-semibold tracking-[-0.01em]">
                {mode === "create"
                  ? t("prompts.editor.titleCreate", "New Prompt")
                  : name || t("prompts.editor.titleEdit", "Edit Prompt")}
              </SheetTitle>
              <p className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                {mode === "edit" ? (
                  <>
                    <span className="truncate font-mono">{usedIn}</span>
                    <span aria-hidden>·</span>
                    <span className="truncate">
                      {rowPrompt?.tenantName ?? t("prompts.table.global", "Global")}
                    </span>
                    {versions[0] && (
                      <>
                        <span aria-hidden>·</span>
                        <span className="shrink-0 tabular-nums">
                          v{versions[0].version}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  t(
                    "prompts.editor.subtitleCreate",
                    "Saved as version 1 for the selected call site",
                  )
                )}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {mode === "edit" && rowPrompt?.tenantId != null && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 pr-1">
                      <Label
                        htmlFor="status-toggle"
                        className="cursor-pointer text-[12px] text-muted-foreground"
                      >
                        {isActive
                          ? t("prompts.status.active", "Active")
                          : t("prompts.status.inactive", "Inactive")}
                      </Label>
                      <Switch
                        id="status-toggle"
                        checked={isActive}
                        onCheckedChange={setIsActive}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[240px] text-xs">
                    {t(
                      "prompts.editor.statusTooltip",
                      "Deactivate this override to fall back to the global default.",
                    )}
                  </TooltipContent>
                </Tooltip>
              )}
              {mode === "edit" && rowPrompt && onDuplicate && onDelete && (
                <PromptRowActions
                  prompt={rowPrompt}
                  alwaysVisible
                  includeStatus={false}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                />
              )}
              <button
                type="button"
                className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                onClick={requestClose}
                aria-label={t("common.close", "Close")}
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex min-h-0 flex-1">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {/* Quiet meta — not a shaded “spec panel” */}
            <section
              aria-label={t("prompts.editor.sectionSpecAria", "Prompt setup")}
              className="shrink-0 px-5 pb-5 pt-3"
            >
              <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                <div className="col-span-12 flex flex-col gap-1 sm:col-span-5">
                  <SpecLabel htmlFor="prompt-name">
                    {t("prompts.editor.name", "Name")}
                  </SpecLabel>
                  <Input
                    id="prompt-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t(
                      "prompts.editor.namePlaceholder",
                      "e.g. Opportunity Analyzer v2",
                    )}
                    className="h-8 text-[13px]"
                  />
                </div>

                <div className="col-span-12 flex flex-col gap-1 sm:col-span-4">
                  <SpecLabel htmlFor="prompt-used-in">
                    {t("prompts.editor.usedIn", "Used In")}
                  </SpecLabel>
                  {mode === "edit" ? (
                    <Input
                      id="prompt-used-in"
                      value={usedIn}
                      disabled
                      className="h-8 font-mono text-[12px]"
                    />
                  ) : (
                    <Popover open={usedInOpen} onOpenChange={setUsedInOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={usedInOpen}
                          className="h-8 w-full cursor-pointer justify-between text-[13px] font-normal"
                        >
                          <span
                            className={cn(
                              "truncate",
                              usedIn
                                ? "font-mono text-[12px]"
                                : "text-muted-foreground",
                            )}
                          >
                            {usedIn ||
                              t(
                                "prompts.editor.usedInPlaceholder",
                                "Select or type…",
                              )}
                          </span>
                          <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-(--radix-popover-trigger-width) p-0"
                        align="start"
                      >
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t(
                              "prompts.editor.usedInSearch",
                              "Type or select…",
                            )}
                            value={usedInSearch}
                            onValueChange={setUsedInSearch}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {t(
                                "prompts.editor.usedInCreateHint",
                                "Type to create a new value",
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {usedInSearch.trim() &&
                                !usedInOptions.some(
                                  (v) =>
                                    v.toLowerCase() ===
                                    usedInSearch.trim().toLowerCase(),
                                ) && (
                                  <CommandItem
                                    value={`__create__${usedInSearch.trim()}`}
                                    onSelect={() => {
                                      applyUsedInSelection(usedInSearch.trim());
                                      setUsedInOpen(false);
                                      setUsedInSearch("");
                                    }}
                                  >
                                    {t(
                                      "prompts.editor.usedInCreate",
                                      'Create "{{value}}"',
                                      { value: usedInSearch.trim() },
                                    )}
                                  </CommandItem>
                                )}
                              {usedInOptions
                                .filter((v) =>
                                  v
                                    .toLowerCase()
                                    .includes(usedInSearch.toLowerCase()),
                                )
                                .map((val) => (
                                  <CommandItem
                                    key={val}
                                    value={val}
                                    onSelect={() => {
                                      applyUsedInSelection(val);
                                      setUsedInOpen(false);
                                      setUsedInSearch("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 size-3.5",
                                        usedIn === val ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span className="font-mono text-[12px]">{val}</span>
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                <div className="col-span-12 flex flex-col gap-1 sm:col-span-3">
                  <SpecLabel>{t("prompts.editor.tenant", "Tenant")}</SpecLabel>
                  {mode === "edit" ? (
                    <Input
                      value={
                        rowPrompt?.tenantName ?? t("prompts.table.global", "Global")
                      }
                      disabled
                      className="h-8 text-[13px]"
                    />
                  ) : (
                    <SearchableCombobox<{ id: string; name: string }>
                      items={tenantOptions}
                      getKey={(tenant) => tenant.id}
                      getLabel={(tenant) => tenant.name}
                      value={tenantId === null ? GLOBAL_TENANT_OPTION_ID : tenantId}
                      onSelect={(tenant) =>
                        setTenantId(
                          tenant.id === GLOBAL_TENANT_OPTION_ID ? null : tenant.id,
                        )
                      }
                      placeholder={t(
                        "prompts.editor.tenantGlobal",
                        "Global (no tenant)",
                      )}
                      searchPlaceholder={t(
                        "prompts.editor.tenantSearch",
                        "Search tenants…",
                      )}
                      triggerClassName="h-8 w-full text-[13px]"
                      closeOnSelect
                    />
                  )}
                </div>

                <div className="col-span-12 flex flex-col gap-1 sm:col-span-4">
                  <SpecLabel>{t("prompts.editor.taskType.label", "Task")}</SpecLabel>
                  <ToggleGroup
                    type="single"
                    value={taskType}
                    onValueChange={(val) => {
                      if (val) applyTaskTypeSelection(val as PromptTaskType);
                    }}
                    aria-label={t("prompts.editor.taskType.label", "Task")}
                    className="relative h-8 w-full gap-0 rounded-sm border border-input-border bg-muted/40 p-0.5 shadow-none"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "pointer-events-none absolute inset-y-0.5 left-0.5 w-[calc((100%-0.25rem)/3)] rounded-[4px] bg-primary-subtle",
                        "transition-transform duration-200",
                        EASE_OUT_QUINT,
                        TASK_THUMB_OFFSET[activeTaskIndex],
                      )}
                    />
                    {TASK_TYPE_OPTIONS.map(
                      ({ value, Icon, labelKey, labelFallback, hintKey, hintFallback }) => (
                        <Tooltip key={value}>
                          <TooltipTrigger asChild>
                            <ToggleGroupItem
                              value={value}
                              size="sm"
                              className={cn(
                                "relative z-10 h-7 flex-1 cursor-pointer rounded-[4px] bg-transparent px-2 text-[12px] font-medium text-muted-foreground",
                                "transition-[color,scale] duration-200",
                                EASE_OUT_QUINT,
                                "hover:bg-transparent hover:text-foreground active:scale-[0.97]",
                                "aria-checked:text-primary-subtle-foreground",
                              )}
                            >
                              <Icon className="size-3.5" aria-hidden />
                              {t(labelKey, labelFallback)}
                            </ToggleGroupItem>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">
                            {t(hintKey, hintFallback)}
                          </TooltipContent>
                        </Tooltip>
                      ),
                    )}
                  </ToggleGroup>
                </div>

                <div className="col-span-12 flex flex-col gap-1 sm:col-span-8">
                  <SpecLabel
                    htmlFor="prompt-model"
                    trailing={
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex size-7 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                            aria-label={t(
                              "prompts.editor.model.familyHintAria",
                              "Model family matching rules",
                            )}
                          >
                            <CircleHelp className="size-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                          {t(
                            "prompts.editor.model.familyHint",
                            "Applied only when the provider family matches the call site; otherwise the service default is used.",
                          )}
                        </TooltipContent>
                      </Tooltip>
                    }
                  >
                    {t("prompts.editor.model.label", "Model")}
                  </SpecLabel>
                  <PromptModelSelect
                    id="prompt-model"
                    value={model}
                    onChange={setModel}
                    models={llmModels}
                    suggestedModel={suggestedModel}
                    taskType={taskType}
                    usedIn={effectiveUsedIn ?? ""}
                    defaultModel={detail?.defaultModel}
                    isLoading={isLoadingModels}
                    isRefreshing={refreshModelsMutation.isPending}
                    onRefresh={() => refreshModelsMutation.mutate()}
                  />
                  <p
                    role="status"
                    className={cn(
                      "min-h-4 text-[11px]",
                      modelHelper?.tone === "warning"
                        ? "text-warning-subtle-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {modelHelper?.message}
                  </p>
                </div>

                <div className="col-span-12 flex flex-col gap-1">
                  <SpecLabel htmlFor="prompt-labels">
                    {t("prompts.editor.labels", "Labels")}
                  </SpecLabel>
                  <MultiTagInput
                    id="prompt-labels"
                    options={knownLabels.map((label) => ({ id: label, label }))}
                    value={labels}
                    onChange={setLabels}
                    placeholder={t("prompts.editor.labelsPlaceholder", "Add labels…")}
                  />
                </div>
              </div>
            </section>

            {/* Template is the hero, with its variables docked to it */}
            <section className="flex min-h-0 flex-1 flex-col px-5 pb-4 pt-4">
              <div className="mb-1.5 flex h-5 items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Label
                    id="prompt-content-label"
                    className="text-[12px] font-medium tracking-[-0.01em] text-muted-foreground"
                  >
                    {t("prompts.editor.content", "Content")}
                  </Label>
                  {/* Browsing history is a mode, not a hazard, and it changes only
                      the template — so it is marked on the template, in the tone
                      the rail uses for the row you picked. */}
                  {isViewingHistory && viewedVersion ? (
                    <span className="shrink-0 rounded-sm bg-primary-subtle px-1.5 py-px text-[11px] font-medium tabular-nums text-primary-subtle-foreground">
                      {t("prompts.editor.viewingChip", "Viewing v{{version}}", {
                        version: viewedVersion.version,
                      })}
                    </span>
                  ) : null}
                </div>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {t("prompts.editor.contentChars", "{{count}} chars", {
                    count: content.length,
                  })}
                </span>
              </div>

              <div
                className={cn(
                  "flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border focus-within:border-input-focus-border",
                  isViewingHistory ? "border-primary/40" : "border-border",
                )}
              >
                <div
                  role="group"
                  aria-labelledby="prompt-content-label"
                  className="min-h-0 flex-1 overflow-hidden"
                >
                  <PromptEditor
                    value={content}
                    onChange={setContent}
                    height="100%"
                    className="h-full min-h-[280px]"
                    variables={variables}
                    revealLine={revealLine}
                  />
                </div>

                <div className="flex shrink-0 items-center gap-2 border-t border-border bg-muted/30 px-2 py-1.5">
                  <Label
                    htmlFor="prompt-variables"
                    className="shrink-0 cursor-pointer pl-1 text-[12px] font-medium tracking-[-0.01em] text-muted-foreground"
                  >
                    {t("prompts.editor.variables", "Variables")}
                  </Label>
                  <MultiTagInput
                    id="prompt-variables"
                    className="min-w-0 flex-1 border-transparent bg-transparent px-2"
                    options={registryVariables.map((v) => ({ id: v, label: v }))}
                    value={variables}
                    onChange={setVariables}
                    placeholder={t(
                      "prompts.editor.variablesPlaceholder",
                      "Add variables…",
                    )}
                  />
                  {variables.length > 0 && onOpenPrompt && onOpenReplace ? (
                    <div className="flex max-w-[280px] shrink-0 flex-wrap justify-end gap-1">
                      {variables.map((variable) => (
                        <VariableUsageButton
                          key={variable}
                          name={variable}
                          onOpenPrompt={onOpenPrompt}
                          onOpenReplace={onOpenReplace}
                        />
                      ))}
                    </div>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                        aria-label={t(
                          "prompts.editor.variablesHintAria",
                          "How variables work",
                        )}
                      >
                        <CircleHelp className="size-3.5" aria-hidden />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-xs">
                      {t("prompts.editor.variablesHint", "{{syntax}} in content", {
                        syntax: "{{name}}",
                      })}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </section>

            {/* The only rule left in the drawer: the action bar is pinned while the
                editor scrolls behind it, so the edge is load-bearing. */}
            <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border-subtle px-5 py-3">
              <p
                role="status"
                className={cn(
                  "min-w-0 truncate text-[11px]",
                  saveBlockedReason
                    ? "text-warning-subtle-foreground"
                    : "text-muted-foreground",
                )}
              >
                {saveBlockedReason ??
                  (isDirty
                    ? t("prompts.editor.unsavedChanges", "Unsaved changes")
                    : "")}
              </p>
              <div className="flex shrink-0 items-center gap-2">
                {isViewingHistory ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="cursor-pointer"
                    onClick={handleBackToLatest}
                  >
                    {t("prompts.editor.backToLatest", "Back to latest")}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={requestClose}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                {/* The consequence belongs on the control that causes it, which is
                    why the old banner had to spell it out in a sentence. */}
                <Button
                  size="sm"
                  className="cursor-pointer"
                  onClick={handleSave}
                  disabled={isSaving || saveBlockedReason !== null}
                >
                  {isSaving
                    ? t("prompts.editor.saving", "Saving…")
                    : mode === "create"
                      ? t("common.create", "Create")
                      : isViewingHistory
                        ? t(
                            "prompts.editor.saveAsNewVersion",
                            "Save as new version",
                          )
                        : t("prompts.editor.saveChanges", "Save Changes")}
                </Button>
              </div>
            </div>
          </div>

          {/* The versions rail is recessed by tone rather than fenced by a rule —
              `muted` is a 1% gray that cannot carry a surface on its own. */}
          {mode === "edit" && (
            <aside className="flex w-[248px] shrink-0 flex-col bg-[color-mix(in_srgb,var(--foreground)_4%,transparent)]">
              <div className="flex shrink-0 items-center justify-between gap-2 px-3 pb-1 pt-3">
                <h3 className="text-[12px] font-medium tracking-[-0.01em] text-muted-foreground">
                  {t("prompts.editor.versions", "Versions")}
                </h3>
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {versions.length}
                </span>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
                <VersionTimeline
                  versions={versionEntries}
                  activeVersionId={viewingVersionId ?? latestVersionId}
                  onSelect={handleVersionSelect}
                  onRevert={handleRevert}
                  labels={{
                    latest: t("prompts.editor.timeline.latest", "Latest"),
                    revert: t("prompts.editor.timeline.revert", "Revert"),
                    empty: t("prompts.editor.timeline.empty", "No versions yet."),
                    select: t("prompts.editor.timeline.select", "Select version"),
                    today: t("prompts.editor.timeline.today", "Today"),
                  }}
                />
              </div>
            </aside>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
